import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, X, Mail, CheckCircle2 } from "lucide-react";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    PrimaryButton,
    GhostButton,
} from "@/screens/auth/_ui";
import {
    clearSignupDraft,
    getSignupDraft,
    updateSignupDraft,
} from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";

const MAX_DATAURL_BYTES = 1.5 * 1024 * 1024; // ~1.5MB after compression

// Compress image client-side to a square data URL
const fileToAvatarDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const img = new Image();
            img.onerror = reject;
            img.onload = () => {
                const size = 480;
                const canvas = document.createElement("canvas");
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext("2d");
                // cover-fit crop to square
                const ratio = Math.max(size / img.width, size / img.height);
                const w = img.width * ratio;
                const h = img.height * ratio;
                ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
                const out = canvas.toDataURL("image/jpeg", 0.82);
                resolve(out);
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });

const SignupAvatar = () => {
    const navigate = useNavigate();
    const { signUp } = useAuth();
    const fileRef = useRef(null);
    const [avatar, setAvatar] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [emailSentTo, setEmailSentTo] = useState(null);

    useEffect(() => {
        const d = getSignupDraft();
        if (!d?.fullName || !d?.password) {
            navigate("/auth/signup", { replace: true });
            return;
        }
        setAvatar(d.avatar || null);
    }, [navigate]);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Please pick an image file");
            return;
        }
        try {
            setBusy(true);
            const url = await fileToAvatarDataUrl(file);
            if (url.length > MAX_DATAURL_BYTES * 1.37) {
                // 1.37x because base64 is ~33% bigger than raw bytes
                setError("Image is too large. Try another one.");
                return;
            }
            setAvatar(url);
            updateSignupDraft({ avatar: url });
            setError(null);
        } catch {
            setError("Couldn't load that image.");
        } finally {
            setBusy(false);
        }
    };

    const finishSignup = async (withAvatar) => {
        const d = getSignupDraft() || {};
        const draftForSignup = {
            ...d,
            avatar: withAvatar ? d.avatar || avatar : null,
        };
        setBusy(true);

        // Email signups: Supabase email-confirmation is the source of truth.
        // We do NOT create a local session — the user must click the email
        // link first. After confirmation, `onAuthStateChange` (in
        // AuthContext) auto-creates the local record and signs them in.
        if (d.method === "email" && d.email && d.password && supabase) {
            try {
                const { error: sbErr } = await supabase.auth.signUp({
                    email: d.email,
                    password: d.password,
                    options: {
                        emailRedirectTo: `${window.location.origin}/home`,
                        data: {
                            fullName: d.fullName || null,
                            username: d.username || null,
                            accountType: d.accountType || "personal",
                            avatar: draftForSignup.avatar || null,
                        },
                    },
                });
                setBusy(false);
                if (sbErr) {
                    const msg = (sbErr.message || "").toLowerCase();
                    if (msg.includes("already registered") || msg.includes("already exists")) {
                        setError("This email is already registered. Try logging in instead.");
                        return;
                    }
                    setError(sbErr.message || "Could not create your account. Please try again.");
                    return;
                }
                clearSignupDraft();
                setEmailSentTo(d.email);
                return;
            } catch (err) {
                setBusy(false);
                setError(err?.message || "Could not reach the auth service. Please try again.");
                return;
            }
        }

        // Phone signups (or no Supabase configured): create the local
        // account record and sign in immediately. Phone OTP was already
        // verified in the previous step.
        const res = signUp(draftForSignup);
        if (!res.ok) {
            setBusy(false);
            setError(res.error);
            return;
        }
        clearSignupDraft();
        setBusy(false);
        // Business accounts skip the personal "interests" onboarding.
        // They go straight to verification (since admin must approve before
        // the verified badge appears).
        if (d.accountType === "business") {
            navigate("/verification/business?upgraded=1", { replace: true });
            return;
        }
        // Personal accounts proceed to onboarding (Step 1: Intents)
        navigate("/onboarding", { replace: true });
    };

    return (
        <AuthScreen testId="signup-avatar-screen">
            {emailSentTo ? (
                <EmailVerifyInterstitial
                    email={emailSentTo}
                    onGoLogin={() => navigate("/auth/login", { replace: true })}
                />
            ) : (
                <>
            <ScreenHeader title="Profile photo" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        Add a profile photo
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        Optional — you can always add one later.
                    </p>
                </div>

                <div className="mt-10 flex flex-col items-center gap-4 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    <div
                        className="relative h-40 w-40 rounded-full overflow-hidden bg-secondary flex items-center justify-center shadow-card border border-border"
                        data-testid="signup-avatar-preview"
                    >
                        {avatar ? (
                            <img
                                src={avatar}
                                alt="Profile preview"
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <Camera size={36} className="text-muted-foreground" strokeWidth={1.6} />
                        )}

                        {avatar && (
                            <button
                                type="button"
                                data-testid="signup-avatar-remove"
                                onClick={() => {
                                    setAvatar(null);
                                    updateSignupDraft({ avatar: null });
                                }}
                                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/90 border border-border shadow-soft flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
                                aria-label="Remove photo"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <input
                        ref={fileRef}
                        data-testid="signup-avatar-file"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    <button
                        data-testid="signup-avatar-pick"
                        onClick={() => fileRef.current?.click()}
                        className="h-11 px-5 rounded-full bg-primary/10 text-primary font-medium text-[13.5px] hover:bg-primary/15 active:scale-[0.98] transition-[transform,background-color] duration-200"
                    >
                        {avatar ? "Change photo" : "Choose from gallery"}
                    </button>

                    {error && (
                        <p
                            data-testid="signup-avatar-error"
                            className="text-[12.5px] text-destructive font-medium"
                        >
                            {error}
                        </p>
                    )}
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton
                    testId="signup-avatar-finish"
                    onClick={() => finishSignup(true)}
                    loading={busy}
                >
                    {avatar ? "Finish" : "Continue"}
                </PrimaryButton>
                <GhostButton
                    testId="signup-avatar-skip"
                    onClick={() => finishSignup(false)}
                >
                    Skip for now
                </GhostButton>
            </AuthFooter>
                </>
            )}
        </AuthScreen>
    );
};

export default SignupAvatar;

const EmailVerifyInterstitial = ({ email, onGoLogin }) => {
    const [resending, setResending] = useState(false);
    const [resendOk, setResendOk] = useState(false);
    const [resendErr, setResendErr] = useState(null);

    const resend = async () => {
        if (resending || !supabase) return;
        setResending(true);
        setResendOk(false);
        setResendErr(null);
        try {
            const { error: e } = await supabase.auth.resend({
                type: "signup",
                email,
                options: { emailRedirectTo: `${window.location.origin}/home` },
            });
            if (e) {
                setResendErr(e.message || "Could not resend the email. Please try again.");
            } else {
                setResendOk(true);
            }
        } catch {
            setResendErr("Could not resend the email. Please try again.");
        }
        setResending(false);
    };

    const openMail = () => {
        // Try to open the user's default mail handler. Falls back to a tab.
        const at = String(email || "").split("@")[1] || "";
        const webmail = {
            "gmail.com": "https://mail.google.com",
            "googlemail.com": "https://mail.google.com",
            "yahoo.com": "https://mail.yahoo.com",
            "outlook.com": "https://outlook.live.com/mail",
            "hotmail.com": "https://outlook.live.com/mail",
            "icloud.com": "https://www.icloud.com/mail",
            "me.com": "https://www.icloud.com/mail",
        }[at.toLowerCase()];
        if (webmail) {
            window.open(webmail, "_blank", "noopener");
            return;
        }
        // mailto: opens the OS default mail client.
        window.location.href = `mailto:${email}`;
    };

    return (
        <>
            <ScreenHeader title="Verify your email" />
            <AuthBody>
                <div
                    className="pt-10 flex flex-col items-center text-center animate-fade-up"
                    data-testid="signup-email-sent"
                >
                    <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                        <Mail size={28} />
                    </div>
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        Verify your email
                    </h2>
                    <p className="mt-2 text-[13.5px] text-muted-foreground max-w-xs">
                        We've sent a confirmation link to{" "}
                        <span className="text-foreground font-medium">{email}</span>. Please verify
                        your account before signing in.
                    </p>
                    {resendOk && (
                        <p
                            data-testid="signup-email-resent"
                            className="mt-3 text-[12px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"
                        >
                            <CheckCircle2 size={13} />
                            Confirmation email sent again. Check your inbox.
                        </p>
                    )}
                    {resendErr && (
                        <p data-testid="signup-email-resend-error" className="mt-3 text-[12px] text-destructive font-medium">
                            {resendErr}
                        </p>
                    )}
                </div>
            </AuthBody>
            <AuthFooter>
                <PrimaryButton testId="signup-email-open-app" onClick={openMail}>
                    Open email app
                </PrimaryButton>
                <GhostButton
                    testId="signup-email-resend"
                    onClick={resend}
                    disabled={resending}
                >
                    {resending ? "Resending…" : "Resend confirmation email"}
                </GhostButton>
                <GhostButton testId="signup-email-back-login" onClick={onGoLogin}>
                    Back to login
                </GhostButton>
            </AuthFooter>
        </>
    );
};

